import { useEffect, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import {
  acknowledgeReview, closeCycle, createCycle, deleteGoal, launchCycle, loadCycles, loadDirectory, loadGoals, loadReviews, saveGoal,
  submitManagerReview, submitSelfReview, type Goal, type Review, type ReviewCycle,
} from "../data";
import type { DirectoryRow, Workspace } from "../types";

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);
const stars = (n: number | null) => (n ? "★".repeat(n) + "☆".repeat(5 - n) : "—");
export const reviewTone: Record<Review["status"], string> = { self: "warning", manager: "info", completed: "success", acknowledged: "success" };

/** Performance: review cycles with ratings, every review, and goals. Managers see their team. */
export function Performance({ ws, onOpen }: { ws: Workspace; onOpen: (id: string) => void }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const hr = ws.permissions.has("performance.manage");
  const [tab, setTab] = useState<"reviews" | "goals">("reviews");
  const [cycles, setCycles] = useState<ReviewCycle[]>([]);
  const [reviews, setReviews] = useState<Review[] | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [cycle, setCycle] = useState<string>("");
  const [open, setOpen] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = async () => {
    const [c, r, g] = await Promise.all([loadCycles(cid), loadReviews(cid), loadGoals(cid)]);
    setCycles(c); setReviews(r); setGoals(g);
    if (!cycle && c.length) setCycle((c.find((x) => x.status === "active") ?? c[0]).id);
  };
  useEffect(() => { void refresh().catch((e) => setMsg({ ok: false, text: e.message })); void loadDirectory(cid).then(setPeople).catch(() => setPeople([])); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const name = (id: string | null) => { const p = people.find((x) => x.employment_id === id); return p ? pick(p.full_name, p.full_name_ar) : "—"; };
  const cur = cycles.find((c) => c.id === cycle);
  const rows = (reviews ?? []).filter((r) => r.cycle_id === cycle && r.employment_id !== ws.membership.employment_id);
  const dist = [1, 2, 3, 4, 5].map((n) => rows.filter((r) => r.manager_rating === n).length);
  const maxD = Math.max(1, ...dist);
  async function run(fn: () => Promise<unknown>, done?: string) {
    setMsg(null);
    try { await fn(); await refresh(); if (done) setMsg({ ok: true, text: done }); }
    catch (e) { setMsg({ ok: false, text: errText(e, t("errorGeneric")) }); }
  }
  const review = rows.find((r) => r.id === open);

  return (
    <div className="page">
      <div className="tabs" role="tablist">
        {([["reviews", "pfReviews"], ["goals", "pfGoals"]] as ["reviews" | "goals", StringKey][]).map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "tab current" : "tab"} onClick={() => setTab(k)}>{t(l)}</button>
        ))}
      </div>
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      {tab === "goals" ? <GoalsTab ws={ws} goals={goals.filter((g) => g.employment_id !== ws.membership.employment_id)} people={people} onChanged={refresh} /> : (
        <>
          <section className="card">
            <div className="row spread">
              <select aria-label={t("pfCycle")} value={cycle} onChange={(e) => { setCycle(e.target.value); setOpen(null); }}>
                {cycles.map((c) => <option key={c.id} value={c.id}>{c.name} · {t(`pfStatus_${c.status}` as StringKey)}</option>)}
              </select>
              {hr && cur && (
                <div className="row">
                  {cur.status !== "closed" && <button type="button" className="btn secondary small" onClick={() => run(() => launchCycle(cur.id), t("pfLaunched"))}>{cur.status === "draft" ? t("pfLaunch") : t("pfAddMissing")}</button>}
                  {cur.status === "active" && <button type="button" className="btn secondary small" onClick={() => run(() => closeCycle(cur.id), t("pfClosed"))}>{t("pfClose")}</button>}
                </div>
              )}
            </div>
            {cur && <p className="muted small-note">{t(`pfKind_${cur.kind}` as StringKey)} · {cur.period_start} → {cur.period_end}{cur.manager_due ? ` · ${t("pfManagerDue")} ${cur.manager_due}` : ""}</p>}
            {rows.length > 0 && (
              <div className="kpis">
                <div className="stat"><span className="stat-label">{t("pfSelfDone")}</span><span className="stat-value">{rows.filter((r) => r.self_submitted_at).length}/{rows.length}</span></div>
                <div className="stat"><span className="stat-label">{t("pfManagerDone")}</span><span className="stat-value">{rows.filter((r) => r.manager_submitted_at).length}/{rows.length}</span></div>
                <div className="stat"><span className="stat-label">{t("pfAverage")}</span><span className="stat-value">{(() => { const xs = rows.filter((r) => r.manager_rating); return xs.length ? (xs.reduce((a, r) => a + (r.manager_rating ?? 0), 0) / xs.length).toFixed(2) : "—"; })()}</span></div>
              </div>
            )}
            {rows.some((r) => r.manager_rating) && (
              <ul className="hbars" aria-label={t("pfDistribution")}>
                {dist.map((n, i) => <li key={i}><span>{stars(i + 1)}</span><span className="track"><span className="fill" style={{ width: `${(100 * n) / maxD}%` }} /></span><span className="n">{n}</span></li>)}
              </ul>
            )}
          </section>
          {review && <ManagerReview ws={ws} r={review} cycle={cur!} name={name(review.employment_id)} onDone={async () => { setOpen(null); await refresh(); }} onOpen={onOpen} />}
          <section className="card">
            {reviews === null ? <p className="muted">{t("loading")}</p> : rows.length === 0 ? <p className="muted">{t("pfNoReviews")}</p> : (
              <div className="table-wrap"><table>
                <thead><tr><th>{t("employee")}</th><th className="hide-sm">{t("pfReviewer")}</th><th>{t("pfSelf")}</th><th>{t("pfManager")}</th><th>{t("status")}</th></tr></thead>
                <tbody>{rows.map((r) => (
                  <tr key={r.id}>
                    <td><button type="button" className="link strong" onClick={() => setOpen(open === r.id ? null : r.id)}>{name(r.employment_id)}</button></td>
                    <td className="hide-sm">{name(r.reviewer_employment)}</td>
                    <td className="stars">{stars(r.self_rating)}</td>
                    <td className="stars">{stars(r.manager_rating)}</td>
                    <td><span className={`badge ${reviewTone[r.status]}`}>{t(`pfR_${r.status}` as StringKey)}</span>{r.probation_outcome && <span className={`badge ${r.probation_outcome === "confirm" ? "success" : "danger"}`}> {t(`pfOutcome_${r.probation_outcome}` as StringKey)}</span>}</td>
                  </tr>
                ))}</tbody>
              </table></div>
            )}
          </section>
          {hr && <NewCycle ws={ws} onDone={async (id) => { await refresh(); setCycle(id); }} />}
        </>
      )}
    </div>
  );
}

function ManagerReview({ ws, r, cycle, name, onDone, onOpen }: { ws: Workspace; r: Review; cycle: ReviewCycle; name: string; onDone: () => Promise<void>; onOpen: (id: string) => void }) {
  const { t } = useI18n();
  const done = !!r.manager_submitted_at;
  const [f, setF] = useState({ rating: String(r.manager_rating ?? ""), comments: r.manager_comments ?? "", strengths: r.strengths ?? "", improvements: r.improvements ?? "", outcome: r.probation_outcome ?? "" });
  const [err, setErr] = useState<string | null>(null);
  async function send(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    try { await submitManagerReview(r.id, Number(f.rating), f.comments, f.strengths, f.improvements, cycle.kind === "probation" ? f.outcome || null : null); await onDone(); }
    catch (x) { setErr(errText(x, t("errorGeneric"))); }
  }
  return (
    <form className="card" onSubmit={send}>
      <div className="row spread"><h2>{name}</h2><button type="button" className="link" onClick={() => onOpen(r.employment_id)}>{t("obOpenProfile")}</button></div>
      {r.self_submitted_at ? (
        <div className="quote"><strong>{t("pfSelf")}: <span className="stars">{stars(r.self_rating)}</span></strong>{r.self_comments && <p>{r.self_comments}</p>}</div>
      ) : <p className="muted small-note">{t("pfNoSelfYet")}</p>}
      <fieldset disabled={done || ws.membership.employment_id === r.employment_id} className="stack">
        <div className="field-row">
          <label className="field"><span>{t("pfRating")}</span>
            <select value={f.rating} onChange={(e) => setF({ ...f, rating: e.target.value })} required>
              <option value="">—</option>{[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{stars(n)} · {t(`pfScale_${n}` as StringKey)}</option>)}
            </select></label>
          {cycle.kind === "probation" && (
            <label className="field"><span>{t("pfOutcome")}</span>
              <select value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value })} required>
                <option value="">—</option><option value="confirm">{t("pfOutcome_confirm")}</option><option value="end">{t("pfOutcome_end")}</option>
              </select></label>
          )}
        </div>
        <label className="field"><span>{t("pfComments")}</span><textarea rows={3} value={f.comments} onChange={(e) => setF({ ...f, comments: e.target.value })} required /></label>
        <div className="field-row">
          <label className="field"><span>{t("pfStrengths")}</span><input value={f.strengths} onChange={(e) => setF({ ...f, strengths: e.target.value })} /></label>
          <label className="field"><span>{t("pfImprovements")}</span><input value={f.improvements} onChange={(e) => setF({ ...f, improvements: e.target.value })} /></label>
        </div>
      </fieldset>
      {r.employee_comment && <p className="muted small-note">{t("pfEmployeeSaid")}: “{r.employee_comment}”</p>}
      {cycle.kind === "probation" && f.outcome === "end" && <p className="alert warning">{t("pfEndHelp")}</p>}
      {err && <p className="alert danger" role="alert">{err}</p>}
      {!done && <div className="actions"><button className="btn primary">{t("pfSubmit")}</button></div>}
    </form>
  );
}

function NewCycle({ ws, onDone }: { ws: Workspace; onDone: (id: string) => Promise<void> }) {
  const { t } = useI18n();
  const y = new Date().getFullYear();
  const [f, setF] = useState({ name: `Annual review ${y}`, kind: "annual", start: `${y}-01-01`, end: `${y}-12-31`, self: "", manager: "" });
  const [err, setErr] = useState<string | null>(null);
  async function save(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    try { const id = await createCycle(ws.membership.company_id, { name: f.name, kind: f.kind as ReviewCycle["kind"], period_start: f.start, period_end: f.end, self_due: f.self || null, manager_due: f.manager || null }); await onDone(id); }
    catch (x) { setErr(errText(x, t("errorGeneric"))); }
  }
  return (
    <form className="card" onSubmit={save}>
      <h2>{t("pfNewCycle")}</h2>
      <div className="field-row">
        <label className="field"><span>{t("rwTitle")}</span><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></label>
        <label className="field"><span>{t("otKind")}</span>
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>{["annual", "mid_year", "quarterly", "probation"].map((k) => <option key={k} value={k}>{t(`pfKind_${k}` as StringKey)}</option>)}</select></label>
        <label className="field"><span>{t("from")}</span><input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} required /></label>
        <label className="field"><span>{t("to")}</span><input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} required /></label>
      </div>
      <div className="field-row">
        <label className="field"><span>{t("pfSelfDue")}</span><input type="date" value={f.self} onChange={(e) => setF({ ...f, self: e.target.value })} /></label>
        <label className="field"><span>{t("pfManagerDue")}</span><input type="date" value={f.manager} onChange={(e) => setF({ ...f, manager: e.target.value })} /></label>
      </div>
      {f.kind === "probation" && <p className="muted small-note">{t("pfProbationHelp")}</p>}
      {err && <p className="alert danger" role="alert">{err}</p>}
      <div className="actions"><button className="btn primary">{t("rwCreate")}</button></div>
    </form>
  );
}

function GoalsTab({ ws, goals, people, onChanged }: { ws: Workspace; goals: Goal[]; people: DirectoryRow[]; onChanged: () => Promise<void> }) {
  const { t, pick } = useI18n();
  const [q, setQ] = useState("");
  const [f, setF] = useState({ emp: "", title: "", weight: "20", due: "" });
  const [err, setErr] = useState<string | null>(null);
  const byPerson = new Map<string, Goal[]>();
  for (const g of goals) byPerson.set(g.employment_id, [...(byPerson.get(g.employment_id) ?? []), g]);
  const ids = [...byPerson.keys()].filter((id) => { const p = people.find((x) => x.employment_id === id); return !q || (p && `${p.full_name} ${p.full_name_ar ?? ""}`.toLowerCase().includes(q.toLowerCase())); });
  const name = (id: string) => { const p = people.find((x) => x.employment_id === id); return p ? pick(p.full_name, p.full_name_ar) : "—"; };
  async function add(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    try { await saveGoal(null, f.emp, f.title, Number(f.weight), f.due || null, 0, null); setF({ ...f, title: "" }); await onChanged(); }
    catch (x) { setErr(errText(x, t("errorGeneric"))); }
  }
  return (
    <>
      <section className="card">
        <input className="search" type="search" placeholder={t("search")} aria-label={t("search")} value={q} onChange={(e) => setQ(e.target.value)} />
        {ids.length === 0 ? <p className="muted">{t("pfNoGoals")}</p> : (
          <ul className="req-list">
            {ids.map((id) => {
              const gs = byPerson.get(id)!;
              const avg = Math.round(gs.reduce((a, g) => a + g.progress * (g.weight || 1), 0) / Math.max(1, gs.reduce((a, g) => a + (g.weight || 1), 0)));
              return (
                <li key={id} className="goal-person">
                  <div className="row spread"><strong>{name(id)}</strong><span className="row"><span className="meter" style={{ width: 90 }}><span style={{ width: `${avg}%` }} /></span><span className="mono small-note">{avg}%</span></span></div>
                  <ul className="plain-list small-note">{gs.map((g) => <li key={g.id} className="row spread"><span>{g.title} <span className="muted">· {g.weight}%</span></span><span className="row"><span className="mono">{g.progress}%</span>
                    <span className={`badge ${g.status === "achieved" ? "success" : g.status === "active" ? "info" : "neutral"}`}>{t(`gStatus_${g.status}` as StringKey)}</span>
                    <button type="button" className="link" aria-label={t("payRemove")} onClick={async () => { await deleteGoal(g.id); await onChanged(); }}>×</button></span></li>)}</ul>
                </li>
              );
            })}
          </ul>
        )}
      </section>
      <form className="card" onSubmit={add}>
        <h2>{t("pfAddGoal")}</h2>
        <div className="field-row">
          <label className="field"><span>{t("employee")}</span>
            <select value={f.emp} onChange={(e) => setF({ ...f, emp: e.target.value })} required><option value="">—</option>
              {people.filter((p) => p.status !== "terminated" && p.employment_id !== ws.membership.employment_id).map((p) => <option key={p.employment_id} value={p.employment_id}>{pick(p.full_name, p.full_name_ar)}</option>)}</select></label>
          <label className="field"><span>{t("pfGoal")}</span><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required /></label>
          <label className="field"><span>{t("pfWeight")}</span><input type="number" min="0" max="100" value={f.weight} onChange={(e) => setF({ ...f, weight: e.target.value })} /></label>
          <label className="field"><span>{t("pfDue")}</span><input type="date" value={f.due} onChange={(e) => setF({ ...f, due: e.target.value })} /></label>
        </div>
        {err && <p className="alert danger" role="alert">{err}</p>}
        <div className="actions"><button className="btn secondary">{t("payAdd")}</button></div>
      </form>
    </>
  );
}

/** Self-service: my goals (update progress) and my reviews (self-review, acknowledge). */
export function MyPerformance({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const cid = ws.membership.company_id;
  const emp = ws.membership.employment_id;
  const [goals, setGoals] = useState<Goal[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [cycles, setCycles] = useState<ReviewCycle[]>([]);
  const [self, setSelf] = useState({ rating: "", comments: "" });
  const [ack, setAck] = useState("");
  const [newGoal, setNewGoal] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const refresh = async () => {
    const [g, r, c] = await Promise.all([loadGoals(cid), loadReviews(cid), loadCycles(cid)]);
    setGoals(g.filter((x) => x.employment_id === emp)); setReviews(r.filter((x) => x.employment_id === emp)); setCycles(c);
  };
  useEffect(() => { void refresh(); }, [cid, emp]); // eslint-disable-line react-hooks/exhaustive-deps
  async function run(fn: () => Promise<unknown>) { setErr(null); try { await fn(); await refresh(); } catch (x) { setErr(errText(x, t("errorGeneric"))); } }
  const cycleOf = (id: string) => cycles.find((c) => c.id === id);
  const open = reviews.filter((r) => cycleOf(r.cycle_id)?.status === "active" || r.status === "completed");
  if (goals.length === 0 && reviews.length === 0) return null;
  return (
    <section className="card">
      <h2>{t("pfMine")}</h2>
      {err && <p className="alert danger" role="alert">{err}</p>}
      {open.map((r) => (
        <div key={r.id} className="stack my-review">
          <div className="row spread"><strong>{cycleOf(r.cycle_id)?.name}</strong><span className={`badge ${reviewTone[r.status]}`}>{t(`pfR_${r.status}` as StringKey)}</span></div>
          {r.status === "self" && cycleOf(r.cycle_id)?.kind !== "probation" && (
            <form className="stack" onSubmit={(e) => { e.preventDefault(); void run(() => submitSelfReview(r.id, Number(self.rating), self.comments)); }}>
              <label className="field"><span>{t("pfMyRating")}</span>
                <select value={self.rating} onChange={(e) => setSelf({ ...self, rating: e.target.value })} required><option value="">—</option>{[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{stars(n)} · {t(`pfScale_${n}` as StringKey)}</option>)}</select></label>
              <label className="field"><span>{t("pfMyComments")}</span><textarea rows={3} value={self.comments} onChange={(e) => setSelf({ ...self, comments: e.target.value })} required /></label>
              <div className="actions"><button className="btn primary">{t("pfSubmitSelf")}</button></div>
            </form>
          )}
          {r.manager_submitted_at && (
            <div className="quote"><strong>{t("pfManager")}: <span className="stars">{stars(r.manager_rating)}</span></strong>{r.manager_comments && <p>{r.manager_comments}</p>}
              {r.strengths && <p className="small-note">{t("pfStrengths")}: {r.strengths}</p>}{r.improvements && <p className="small-note">{t("pfImprovements")}: {r.improvements}</p>}</div>
          )}
          {r.status === "completed" && (
            <div className="row"><input placeholder={t("pfAckComment")} aria-label={t("pfAckComment")} value={ack} onChange={(e) => setAck(e.target.value)} />
              <button type="button" className="btn primary small" onClick={() => run(() => acknowledgeReview(r.id, ack))}>{t("pfAcknowledge")}</button></div>
          )}
        </div>
      ))}
      <h3 className="sub-head">{t("pfMyGoals")}</h3>
      <ul className="plain-list">
        {goals.map((g) => (
          <li key={g.id} className="row spread">
            <span>{g.title} <span className="muted small-note">· {g.weight}%{g.due_date ? ` · ${g.due_date}` : ""}</span></span>
            <span className="row"><input type="range" min="0" max="100" step="10" aria-label={t("pfProgress")} value={g.progress} disabled={g.status !== "active" && g.status !== "achieved"}
              onChange={(e) => void run(() => saveGoal(g.id, null, null, null, null, Number(e.target.value), null))} /><span className="mono small-note">{g.progress}%</span></span>
          </li>
        ))}
      </ul>
      <form className="row" onSubmit={(e) => { e.preventDefault(); void run(() => saveGoal(null, emp, newGoal, 0, null, 0, null)).then(() => setNewGoal("")); }}>
        <input placeholder={t("pfAddMyGoal")} aria-label={t("pfAddMyGoal")} value={newGoal} onChange={(e) => setNewGoal(e.target.value)} required />
        <button className="btn secondary small">{t("payAdd")}</button>
      </form>
    </section>
  );
}
